import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { type IntlShape, type MessageDescriptor, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import SearchIcon from '@atlaskit/icon/core/search';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Select from '@atlaskit/select';
import { token } from '@atlaskit/tokens';

import Flag from '@/contexts/FlagProvider';
import { useFastshiftDispatch, useFastshiftSelector } from '@/state';
import {
	getFastshiftCsProfilerCustomerProfile,
	getFastshiftCsProfilerLinkedTickets,
	getFastshiftCsProfilerProfilingOptions,
} from '@/state/actions';

import {
	buildFastshiftCsProfilerConfluenceExport,
	copyFastshiftCsProfilerConfluenceExportToClipboard,
	EXPORT_CONFLUENCE_SPACE_KEY,
	getFastshiftCsProfilerConfluenceCreatePageUrl,
} from './fastshift-cs-profiler-confluence-export';
import { FastshiftCsProfilerThemeDisplay } from './fastshift-cs-profiler-theme-display';
import { useFastshiftCsProfilerAnalytics } from './hooks/use-fastshift-cs-profiler-analytics';
import { messages } from './messages';

import type {
	FastshiftCsProfilerCustomerProfile,
	FastshiftCsProfilerLinkedTickets,
	FastshiftCsProfilerProfilingOptions,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

type ProfilerSelectOption = {
	label: string;
	value: string;
};

type ProfilerSelection = {
	region: string;
	industry: string;
	segment: string;
};

const regionMessagesByValue: Record<string, MessageDescriptor> = {
	AMER: messages.amerRegionOption,
	EMEA: messages.emeaRegionOption,
	APAC: messages.apacRegionOption,
};

const segmentMessagesByValue: Record<string, MessageDescriptor> = {
	SMB: messages.smbSegmentOption,
	Government: messages.governmentSegmentOption,
};

const styles = cssMap({
	selectorPanel: {
		backgroundColor: token('elevation.surface.overlay'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.small'),
		display: 'block',
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
	},
	filterGrid: {
		alignItems: 'start',
		display: 'grid',
		gap: token('space.150'),
		gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
	},
	profileFieldWrapper: {
		minWidth: '0',
		width: '100%',
	},
	profileFieldLabel: {
		whiteSpace: 'nowrap',
	},
	profileButtonWrapper: {
		alignSelf: 'end',
		minWidth: '0',
		paddingBlockStart: token('space.300'),
		width: '100%',
	},
	emptyStatePanel: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
		paddingBlockEnd: token('space.800'),
		paddingBlockStart: token('space.500'),
		paddingInline: token('space.200'),
		textAlign: 'center',
	},
	emptyStateContent: {
		maxWidth: '440px',
	},
	emptyStateCopy: {
		maxWidth: '400px',
	},
});

const optionFromValue = (value: string): ProfilerSelectOption => ({
	label: value,
	value,
});

const optionFromValueWithLabelMap =
	(messagesByValue: Record<string, MessageDescriptor>, formatMessage: IntlShape['formatMessage']) =>
	(value: string): ProfilerSelectOption => ({
		label: messagesByValue[value] ? formatMessage(messagesByValue[value]) : value,
		value,
	});

const getProfilerOptions = (
	profilingOptions: FastshiftCsProfilerProfilingOptions | null,
	formatMessage: IntlShape['formatMessage'],
) => ({
	regions: (profilingOptions?.regions ?? []).map(
		optionFromValueWithLabelMap(regionMessagesByValue, formatMessage),
	),
	industries: (profilingOptions?.industries ?? []).slice().sort().map(optionFromValue),
	segments: (profilingOptions?.segments ?? []).map(
		optionFromValueWithLabelMap(segmentMessagesByValue, formatMessage),
	),
});

const ProfileField = ({
	inputId,
	label,
	placeholder,
	options,
	value,
	onChange,
	isDisabled,
	isLoading,
}: {
	inputId: string;
	label: string;
	placeholder: string;
	options: ProfilerSelectOption[];
	value: ProfilerSelectOption | null;
	onChange: (option: ProfilerSelectOption | null) => void;
	isDisabled: boolean;
	isLoading: boolean;
}) => (
	<Box xcss={styles.profileFieldWrapper}>
		<Stack space="space.075">
			<Box xcss={styles.profileFieldLabel}>
				<label htmlFor={inputId}>
					<Text size="small" weight="semibold" color="color.text.subtle">
						{label}
					</Text>
				</label>
			</Box>
			<Select<ProfilerSelectOption>
				inputId={inputId}
				options={options}
				placeholder={placeholder}
				value={value}
				onChange={(option) => onChange(option)}
				isDisabled={isDisabled}
				isLoading={isLoading}
				isMulti={false}
				spacing="compact"
			/>
		</Stack>
	</Box>
);

const EmptyProfileState = ({ formatMessage }: { formatMessage: IntlShape['formatMessage'] }) => (
	<Box xcss={styles.emptyStatePanel}>
		<Stack alignInline="center" space="space.150" xcss={styles.emptyStateContent}>
			<Heading size="xsmall" as="h2">
				{formatMessage(messages.emptyStateHeading)}
			</Heading>
			<Box xcss={styles.emptyStateCopy}>
				<Text color="color.text.subtle" align="center">
					{formatMessage(messages.emptyStateDescription)}
				</Text>
			</Box>
		</Stack>
	</Box>
);

const isSelectionMatchingSelectedOptions = ({
	selection,
	selectedRegion,
	selectedIndustry,
	selectedSegment,
}: {
	selection: ProfilerSelection | null;
	selectedRegion: ProfilerSelectOption | null;
	selectedIndustry: ProfilerSelectOption | null;
	selectedSegment: ProfilerSelectOption | null;
}) =>
	Boolean(
		selection &&
		selectedRegion?.value === selection.region &&
		selectedIndustry?.value === selection.industry &&
		selectedSegment?.value === selection.segment,
	);

const ProfileResults = ({
	customerProfileData,
	formatMessage,
	hasCustomerProfileError,
	isCustomerProfileForCurrentSelection,
	isCustomerProfilePending,
	isExportingToConfluence,
	onExportToConfluence,
}: {
	customerProfileData: FastshiftCsProfilerCustomerProfile | null;
	formatMessage: IntlShape['formatMessage'];
	hasCustomerProfileError: boolean;
	isCustomerProfileForCurrentSelection: boolean;
	isCustomerProfilePending: boolean;
	isExportingToConfluence: boolean;
	onExportToConfluence: () => void;
}) => {
	if (hasCustomerProfileError) {
		return <Text color="color.text.danger">{formatMessage(messages.customerProfileError)}</Text>;
	}

	if (!isCustomerProfileForCurrentSelection && !isCustomerProfilePending) {
		return <EmptyProfileState formatMessage={formatMessage} />;
	}

	if (isCustomerProfileForCurrentSelection && customerProfileData) {
		return (
			<FastshiftCsProfilerThemeDisplay
				customerProfile={customerProfileData}
				isExportingToConfluence={isExportingToConfluence}
				onExportToConfluence={onExportToConfluence}
			/>
		);
	}

	return null;
};

export const FastshiftCsProfilerPage = () => {
	const { formatMessage } = useIntl();
	const stargateClient = useBxpWebStargateClient();
	const dispatch = useFastshiftDispatch();
	const { showFlag } = useContext(Flag.Context);
	const {
		sendCsProfilerScreenEvent,
		sendProfilingOptionsDetailsTrackEvent,
		sendCustomerProfileDetailsTrackEvent,
		sendProfileCustomerButtonClickUIEvent,
		sendExportToConfluenceButtonClickUIEvent,
	} = useFastshiftCsProfilerAnalytics();
	const { profilingOptions, customerProfile, linkedTicketsByTheme } = useFastshiftSelector(
		(state) => state.csProfiler,
	);
	const { regions, industries, segments } = useMemo(
		() => getProfilerOptions(profilingOptions.data, formatMessage),
		[formatMessage, profilingOptions.data],
	);
	const [selectedRegion, setSelectedRegion] = useState<ProfilerSelectOption | null>(null);
	const [selectedIndustry, setSelectedIndustry] = useState<ProfilerSelectOption | null>(null);
	const [selectedSegment, setSelectedSegment] = useState<ProfilerSelectOption | null>(null);
	const [submittedSelection, setSubmittedSelection] = useState<ProfilerSelection | null>(null);
	const [isExportingToConfluence, setIsExportingToConfluence] = useState(false);
	const areFiltersLoading = profilingOptions.pending && !profilingOptions.data;
	const areFiltersDisabled = profilingOptions.pending || Boolean(profilingOptions.error);
	const isProfileReady = Boolean(selectedRegion && selectedIndustry && selectedSegment);
	const hasRequestedProfilingOptions = useRef(false);
	const customerProfileData = customerProfile.data;
	const isCustomerProfileForCurrentSelection = isSelectionMatchingSelectedOptions({
		selection: customerProfileData?.selection ?? null,
		selectedRegion,
		selectedIndustry,
		selectedSegment,
	});
	const hasCustomerProfileErrorForCurrentSelection = Boolean(
		customerProfile.error &&
		isSelectionMatchingSelectedOptions({
			selection: submittedSelection,
			selectedRegion,
			selectedIndustry,
			selectedSegment,
		}),
	);

	const fetchProfilingOptions = useCallback(() => {
		dispatch(getFastshiftCsProfilerProfilingOptions({ stargateClient }));
	}, [dispatch, stargateClient]);

	const handleProfileCustomer = useCallback(() => {
		if (!selectedRegion || !selectedIndustry || !selectedSegment) {
			return;
		}

		const selection = {
			region: selectedRegion.value,
			industry: selectedIndustry.value,
			segment: selectedSegment.value,
		};

		setSubmittedSelection(selection);
		sendProfileCustomerButtonClickUIEvent(selection);
		dispatch(
			getFastshiftCsProfilerCustomerProfile({
				stargateClient,
				...selection,
			}),
		);
	}, [
		dispatch,
		selectedIndustry,
		selectedRegion,
		selectedSegment,
		sendProfileCustomerButtonClickUIEvent,
		stargateClient,
	]);

	const handleExportToConfluence = useCallback(async () => {
		if (!customerProfileData) {
			return;
		}

		sendExportToConfluenceButtonClickUIEvent(customerProfileData);
		setIsExportingToConfluence(true);

		try {
			const confluenceExportPromise = (async () => {
				const linkedTicketsForExport: Record<string, FastshiftCsProfilerLinkedTickets> = {};

				await Promise.all(
					customerProfileData.themes.map(async ({ themeDetails }) => {
						const themeName = themeDetails.name;
						const cachedLinkedTickets = linkedTicketsByTheme[themeName]?.data;

						if (cachedLinkedTickets) {
							linkedTicketsForExport[themeName] = cachedLinkedTickets;
							return;
						}

						const linkedTickets = await dispatch(
							getFastshiftCsProfilerLinkedTickets({
								stargateClient,
								theme: themeName,
							}),
						).unwrap();

						linkedTicketsForExport[themeName] = linkedTickets;
					}),
				);

				return buildFastshiftCsProfilerConfluenceExport({
					profile: customerProfileData,
					linkedTicketsByTheme: linkedTicketsForExport,
				});
			})();
			const copied = await copyFastshiftCsProfilerConfluenceExportToClipboard(
				confluenceExportPromise.then(({ html }) => html),
			);
			const { title } = await confluenceExportPromise;

			showFlag({
				id: copied
					? 'fastshift-cs-profiler-confluence-export-success'
					: 'fastshift-cs-profiler-confluence-export-error',
				iconType: copied ? 'success' : 'error',
				title: copied
					? formatMessage(messages.exportSuccessFlagTitle)
					: formatMessage(messages.exportErrorFlagTitle),
				description: copied
					? formatMessage(messages.exportSuccessFlagDescription)
					: formatMessage(messages.exportErrorFlagDescription),
				actions: copied
					? [
							{
								content: formatMessage(messages.exportSuccessFlagAction, {
									spaceKey: EXPORT_CONFLUENCE_SPACE_KEY,
								}),
								href: getFastshiftCsProfilerConfluenceCreatePageUrl(title),
								target: '_blank',
							},
						]
					: undefined,
			});
		} catch {
			showFlag({
				id: 'fastshift-cs-profiler-confluence-export-error',
				iconType: 'error',
				title: formatMessage(messages.exportPreparationErrorFlagTitle),
				description: formatMessage(messages.exportPreparationErrorFlagDescription),
			});
		} finally {
			setIsExportingToConfluence(false);
		}
	}, [
		customerProfileData,
		dispatch,
		formatMessage,
		linkedTicketsByTheme,
		sendExportToConfluenceButtonClickUIEvent,
		showFlag,
		stargateClient,
	]);

	useEffect(() => {
		sendCsProfilerScreenEvent();
	}, [sendCsProfilerScreenEvent]);

	useEffect(() => {
		sendProfilingOptionsDetailsTrackEvent(profilingOptions);
	}, [profilingOptions, sendProfilingOptionsDetailsTrackEvent]);

	useEffect(() => {
		sendCustomerProfileDetailsTrackEvent({
			customerProfile,
			isCustomerProfileForCurrentSelection,
			hasCustomerProfileErrorForCurrentSelection,
			currentSelection: submittedSelection,
		});
	}, [
		customerProfile,
		hasCustomerProfileErrorForCurrentSelection,
		isCustomerProfileForCurrentSelection,
		sendCustomerProfileDetailsTrackEvent,
		submittedSelection,
	]);

	useEffect(() => {
		if (
			!profilingOptions.data &&
			!profilingOptions.pending &&
			!hasRequestedProfilingOptions.current
		) {
			hasRequestedProfilingOptions.current = true;
			fetchProfilingOptions();
		}
	}, [fetchProfilingOptions, profilingOptions.data, profilingOptions.pending]);

	return (
		<Stack space="space.400">
			<Stack space="space.050">
				<Heading size="large" as="h1">
					{formatMessage(messages.pageHeading)}
				</Heading>
				<Text color="color.text.subtle">{formatMessage(messages.pageDescription)}</Text>
			</Stack>

			<Box xcss={styles.selectorPanel}>
				<Stack space="space.150">
					{profilingOptions.error && (
						<Text color="color.text.danger">{formatMessage(messages.profilingOptionsError)}</Text>
					)}

					<Box xcss={styles.filterGrid}>
						<ProfileField
							inputId="fastshift-cs-profiler-region"
							label={formatMessage(messages.regionLabel)}
							placeholder={formatMessage(messages.regionPlaceholder)}
							options={regions}
							value={selectedRegion}
							onChange={setSelectedRegion}
							isDisabled={areFiltersDisabled}
							isLoading={areFiltersLoading}
						/>
						<ProfileField
							inputId="fastshift-cs-profiler-industry"
							label={formatMessage(messages.industryLabel)}
							placeholder={formatMessage(messages.industryPlaceholder)}
							options={industries}
							value={selectedIndustry}
							onChange={setSelectedIndustry}
							isDisabled={areFiltersDisabled}
							isLoading={areFiltersLoading}
						/>
						<ProfileField
							inputId="fastshift-cs-profiler-segment"
							label={formatMessage(messages.segmentLabel)}
							placeholder={formatMessage(messages.segmentPlaceholder)}
							options={segments}
							value={selectedSegment}
							onChange={setSelectedSegment}
							isDisabled={areFiltersDisabled}
							isLoading={areFiltersLoading}
						/>
						<Box xcss={styles.profileButtonWrapper}>
							<Button
								appearance="primary"
								isDisabled={!isProfileReady || customerProfile.pending || areFiltersDisabled}
								isLoading={customerProfile.pending}
								onClick={handleProfileCustomer}
								shouldFitContainer
							>
								<Inline alignBlock="center" alignInline="center" space="space.075">
									<SearchIcon label="" size="small" />
									{formatMessage(messages.profileButton)}
								</Inline>
							</Button>
						</Box>
					</Box>
				</Stack>
			</Box>

			<ProfileResults
				customerProfileData={customerProfileData}
				formatMessage={formatMessage}
				hasCustomerProfileError={hasCustomerProfileErrorForCurrentSelection}
				isCustomerProfileForCurrentSelection={isCustomerProfileForCurrentSelection}
				isCustomerProfilePending={customerProfile.pending}
				isExportingToConfluence={isExportingToConfluence}
				onExportToConfluence={handleExportToConfluence}
			/>
		</Stack>
	);
};
